// Typed fetch client for the EasyHelp admin API. Single source of truth for
// every endpoint the app calls — components never call fetch() directly.
//
// Session is a cookie (HttpOnly) set by the magic-link callback, so requests
// just need `credentials: "include"`. Errors throw `ApiError` for callers to
// surface (the UI maps these to inline error states).
import type {
  ArticleStatus,
  AuditLogEntry,
  Automation,
  AutomationAction,
  AutomationCondition,
  AutomationTrigger,
  Campaign,
  CampaignContent,
  CampaignStatus,
  CampaignType,
  CompanyInfo,
  CompanySettings,
  ContactTimeline,
  ConversationDetail,
  EmailChannelStatus,
  HelpArea,
  HelpArticle,
  HelpTopic,
  ConversationListItem,
  ConversationStatus,
  Invite,
  Locale,
  Me,
  Member,
  Message,
  Notification,
  Priority,
  Project,
  ProjectSettings,
  ReportData,
  ReportPeriod,
  Role,
  SessionInfo,
  SessionUser,
  Tag,
  Team,
  TeamDetail,
  Ticket,
  TicketStatus,
  WebhookDelivery,
  WebhookEndpoint,
} from "./types";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** URL for serving an attachment (image/file) from the admin API. */
export function attachmentUrl(id: string): string {
  return `/api/admin/attachments/${id}`;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

export type ConversationFilters = {
  status?: ConversationStatus;
  channel?: string;
  project?: string;
  limit?: number;
};

function query(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export const api = {
  auth: {
    me: () => get<Me>("/auth/me"),
    requestLogin: (email: string) => post<{ ok: true }>("/auth/request", { email }),
    register: (email: string, companyName: string, name?: string) =>
      post<{ ok: true }>("/auth/register", { email, companyName, name }),
    logout: () => post<{ ok: true }>("/auth/logout"),
    switchCompany: (companyId: string) => post<{ ok: true }>("/auth/switch-company", { companyId }),
  },
  projects: {
    list: () => get<{ projects: Project[] }>("/admin/projects"),
    create: (name: string, allowedOrigins?: string) =>
      post<{ project: Project }>("/admin/projects", { name, allowed_origins: allowedOrigins }),
    update: (id: string, body: { name?: string; allowedOrigins?: string; settings?: Partial<ProjectSettings> }) =>
      patch<{ project: Project }>(`/admin/projects/${id}`, {
        name: body.name,
        allowed_origins: body.allowedOrigins,
        settings: body.settings,
      }),
    rotateKey: (id: string) => post<{ apiKey: string }>(`/admin/projects/${id}/rotate-key`),
    remove: (id: string) => del<{ ok: true }>(`/admin/projects/${id}`),
  },
  channels: {
    // Email channel (Microsoft 365 via Graph), per project.
    emailStatus: (projectId: string) =>
      get<EmailChannelStatus>(`/admin/projects/${projectId}/channels/email`),
    /** Returns the Entra admin-consent URL to redirect the browser to. */
    emailConnect: (projectId: string, mailbox: string) =>
      post<{ url: string }>(`/admin/projects/${projectId}/channels/email/connect`, { mailbox }),
    emailDisconnect: (projectId: string) =>
      del<{ ok: true }>(`/admin/projects/${projectId}/channels/email`),
  },
  company: {
    get: () => get<{ company: CompanyInfo; seats: number; role: Role }>("/admin/company"),
    update: (body: { name?: string; settings?: Partial<CompanySettings> }) =>
      patch<{ company: CompanyInfo }>("/admin/company", body),
    members: () => get<{ members: Member[] }>("/admin/company/members"),
    updateMemberRole: (userId: string, role: Role) =>
      patch<{ ok: true }>(`/admin/company/members/${userId}`, { role }),
    removeMember: (userId: string) => del<{ ok: true }>(`/admin/company/members/${userId}`),
    invites: () => get<{ invites: Invite[] }>("/admin/company/invites"),
    createInvite: (email: string, role: Role) =>
      post<{ ok: true; inviteId: string }>("/admin/company/invites", { email, role }),
    revokeInvite: (id: string) => del<{ ok: true }>(`/admin/company/invites/${id}`),
    audit: (params: { action?: string; before?: string; limit?: number } = {}) =>
      get<{ entries: AuditLogEntry[]; nextCursor: string | null }>(
        `/admin/company/audit${query(params)}`,
      ),
  },
  teams: {
    list: () => get<{ teams: Team[] }>("/admin/teams"),
    detail: (id: string) => get<TeamDetail>(`/admin/teams/${id}`),
    create: (name: string) => post<{ team: { id: string; name: string } }>("/admin/teams", { name }),
    rename: (id: string, name: string) => patch<{ ok: true }>(`/admin/teams/${id}`, { name }),
    remove: (id: string) => del<{ ok: true }>(`/admin/teams/${id}`),
    addMember: (id: string, userId: string) =>
      post<{ ok: true }>(`/admin/teams/${id}/members`, { userId }),
    removeMember: (id: string, userId: string) =>
      del<{ ok: true }>(`/admin/teams/${id}/members/${userId}`),
    addProject: (id: string, projectId: string) =>
      post<{ ok: true }>(`/admin/teams/${id}/projects`, { projectId }),
    removeProject: (id: string, projectId: string) =>
      del<{ ok: true }>(`/admin/teams/${id}/projects/${projectId}`),
  },
  conversations: {
    list: (filters: ConversationFilters = {}) =>
      get<{ conversations: ConversationListItem[] }>(`/admin/conversations${query(filters)}`),
    detail: (id: string) => get<ConversationDetail>(`/admin/conversations/${id}`),
    messages: (id: string) => get<{ messages: Message[] }>(`/admin/conversations/${id}/messages`),
    reply: (id: string, body: string, internalNote = false, mentions: string[] = []) =>
      post<{ message: { id: string } }>(`/admin/conversations/${id}/messages`, {
        body,
        internalNote,
        mentions,
      }),
    update: (
      id: string,
      patchBody: {
        status?: ConversationStatus;
        assigneeUserId?: string | null;
        priority?: Priority | null;
        subject?: string;
        agentUnread?: boolean;
      },
    ) => patch<{ conversation: unknown }>(`/admin/conversations/${id}`, patchBody),
    addTag: (id: string, name: string) =>
      post<{ ok: true; tag: Tag }>(`/admin/conversations/${id}/tags`, { name }),
    removeTag: (id: string, tagId: string) =>
      del<{ ok: true }>(`/admin/conversations/${id}/tags/${tagId}`),
    convertToTicket: (id: string, title?: string, priority?: Priority) =>
      post<{ ticketId: string }>(`/admin/conversations/${id}/convert-to-ticket`, {
        title,
        priority,
      }),
    copilot: (id: string) =>
      post<{ suggestion: string; sources: string[] }>(`/admin/conversations/${id}/copilot`),
  },
  contacts: {
    timeline: (id: string) => get<ContactTimeline>(`/admin/contacts/${id}/timeline`),
  },
  notifications: {
    list: () => get<{ notifications: Notification[]; unreadCount: number }>("/admin/notifications"),
    markRead: (id?: string) => post<{ ok: true }>("/admin/notifications/read", id ? { id } : {}),
  },
  webhooks: {
    list: (project?: string) => get<{ webhooks: WebhookEndpoint[] }>(`/admin/webhooks${query({ project })}`),
    create: (url: string, events: string[], projectId: string) =>
      post<{ webhook: WebhookEndpoint; secret: string }>("/admin/webhooks", { url, events, projectId }),
    update: (id: string, body: { url?: string; events?: string[]; enabled?: boolean }) =>
      patch<{ webhook: WebhookEndpoint }>(`/admin/webhooks/${id}`, body),
    rollSecret: (id: string) => post<{ secret: string }>(`/admin/webhooks/${id}/roll-secret`),
    remove: (id: string) => del<{ ok: true }>(`/admin/webhooks/${id}`),
    deliveries: (id: string) => get<{ deliveries: WebhookDelivery[] }>(`/admin/webhooks/${id}/deliveries`),
  },
  tickets: {
    list: (filters: { status?: TicketStatus; project?: string; assignee?: string } = {}) =>
      get<{ tickets: Ticket[] }>(`/admin/tickets${query(filters)}`),
    detail: (id: string) => get<{ ticket: Ticket }>(`/admin/tickets/${id}`),
    create: (body: {
      projectId: string;
      title: string;
      description?: string;
      priority?: Priority;
      conversationId?: string;
    }) => post<{ ticket: Ticket }>("/admin/tickets", body),
    update: (
      id: string,
      patchBody: {
        title?: string;
        description?: string;
        status?: TicketStatus;
        priority?: Priority;
        assigneeUserId?: string | null;
      },
    ) => patch<{ ticket: Ticket }>(`/admin/tickets/${id}`, patchBody),
    remove: (id: string) => del<{ ok: true }>(`/admin/tickets/${id}`),
  },
  reports: {
    get: (period: ReportPeriod = "30d", project?: string) =>
      get<ReportData>(`/admin/reports${query({ period, project })}`),
  },
  help: {
    // Areas (top of the tree). Help content is scoped per project.
    areas: (project?: string) => get<{ areas: HelpArea[] }>(`/admin/help/areas${query({ project })}`),
    createArea: (body: { key?: string; name: string; projectId: string }) =>
      post<{ area: HelpArea }>("/admin/help/areas", body),
    updateArea: (id: string, body: { key?: string; name?: string; position?: number }) =>
      patch<{ area: HelpArea }>(`/admin/help/areas/${id}`, body),
    removeArea: (id: string) => del<{ ok: true }>(`/admin/help/areas/${id}`),
    // Topics (multi-level within an area).
    topics: (area?: string, project?: string) =>
      get<{ topics: HelpTopic[] }>(`/admin/help/topics${query({ area, project })}`),
    createTopic: (body: {
      areaId: string;
      parentId?: string | null;
      name: string;
      description?: string;
    }) => post<{ topic: HelpTopic }>("/admin/help/topics", body),
    updateTopic: (
      id: string,
      body: { name?: string; description?: string | null; parentId?: string | null; position?: number },
    ) => patch<{ topic: HelpTopic }>(`/admin/help/topics/${id}`, body),
    removeTopic: (id: string) => del<{ ok: true }>(`/admin/help/topics/${id}`),
    // Articles.
    articles: (
      filters: { area?: string; topic?: string; status?: ArticleStatus; search?: string; project?: string } = {},
    ) => get<{ articles: HelpArticle[] }>(`/admin/help/articles${query(filters)}`),
    article: (id: string) => get<{ article: HelpArticle }>(`/admin/help/articles/${id}`),
    createArticle: (body: {
      title: string;
      body?: string;
      areaId: string;
      topicId?: string | null;
      status?: ArticleStatus;
    }) => post<{ article: HelpArticle }>("/admin/help/articles", body),
    updateArticle: (
      id: string,
      body: {
        title?: string;
        body?: string;
        status?: ArticleStatus;
        areaId?: string;
        topicId?: string | null;
      },
    ) => patch<{ article: HelpArticle }>(`/admin/help/articles/${id}`, body),
    removeArticle: (id: string) => del<{ ok: true }>(`/admin/help/articles/${id}`),
  },
  automations: {
    list: (project?: string) => get<{ automations: Automation[] }>(`/admin/automations${query({ project })}`),
    get: (id: string) => get<{ automation: Automation }>(`/admin/automations/${id}`),
    create: (body: {
      name: string;
      trigger: AutomationTrigger;
      conditions?: AutomationCondition[];
      actions?: AutomationAction[];
      enabled?: boolean;
      projectId: string;
    }) => post<{ automation: Automation }>("/admin/automations", body),
    update: (
      id: string,
      body: {
        name?: string;
        trigger?: AutomationTrigger;
        conditions?: AutomationCondition[];
        actions?: AutomationAction[];
        enabled?: boolean;
      },
    ) => patch<{ automation: Automation }>(`/admin/automations/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/admin/automations/${id}`),
  },
  campaigns: {
    list: (project?: string) => get<{ campaigns: Campaign[] }>(`/admin/campaigns${query({ project })}`),
    get: (id: string) => get<{ campaign: Campaign }>(`/admin/campaigns/${id}`),
    create: (body: {
      name: string;
      type: CampaignType;
      status?: CampaignStatus;
      projectId?: string | null;
      audience?: AutomationCondition[];
      content?: CampaignContent;
    }) => post<{ campaign: Campaign }>("/admin/campaigns", body),
    update: (
      id: string,
      body: {
        name?: string;
        type?: CampaignType;
        status?: CampaignStatus;
        projectId?: string | null;
        audience?: AutomationCondition[];
        content?: CampaignContent;
      },
    ) => patch<{ campaign: Campaign }>(`/admin/campaigns/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/admin/campaigns/${id}`),
  },
  account: {
    updateProfile: (body: { name?: string; locale?: Locale; avatarUrl?: string | null }) =>
      patch<{ user: SessionUser }>("/admin/account", body),
    sessions: () => get<{ sessions: SessionInfo[] }>("/admin/account/sessions"),
    revokeSession: (id: string) => del<{ ok: true }>(`/admin/account/sessions/${id}`),
    revokeOtherSessions: () => post<{ ok: true }>("/admin/account/sessions/revoke-others"),
  },
};
